/** Turns race results into display rows: ordering and formatting live here, not in JSX. */
import type { PlayerId, PlayerProfile, RaceFormat, RaceResultEntry } from '@cube-rush/shared';
import { ru } from '../../i18n/ru';
import { formatRaceTime } from '../../util/format';

export interface ResultRow {
  id: PlayerId;
  profile: PlayerProfile;
  /** 1-based place, null without one. */
  place: number | null;
  /**
   * "m:ss.cc", or a dash / "не в гонке" for players without a time. By levels: the finish
   * time; against the clock: when the latest counted level was done.
   */
  time: string;
  /** e.g. "7/10" by levels, "7" against the clock (there is no last level). */
  levels: string;
  /** Completed levels (the score of a race against the clock). */
  levelsDone: number;
  deaths: number;
  isMe: boolean;
}

/** What the rows need to know about the race. */
export interface ResultRace {
  format: RaceFormat;
  /** Levels of a race by levels (3 to 20, the room decides). */
  levelCount: number;
}

/**
 * Players with a place by place (by levels: the finishers; against the clock: everybody with
 * a counted level); then the others by completed levels (more is better) and deaths (fewer is
 * better); players who gave up last.
 */
export function compareResults(a: RaceResultEntry, b: RaceResultEntry): number {
  if (a.place !== null || b.place !== null) {
    if (a.place === null) return 1;
    if (b.place === null) return -1;
    return a.place - b.place;
  }
  if (a.quit !== b.quit) return a.quit ? 1 : -1;
  if (a.levels !== b.levels) return b.levels - a.levels;
  return a.deaths - b.deaths;
}

export function toResultRows(
  results: readonly RaceResultEntry[],
  myId: PlayerId | null,
  race: ResultRace,
): ResultRow[] {
  const timed = race.format === 'time';
  return [...results].sort(compareResults).map((entry) => {
    const tm = timed ? entry.levelTm : entry.finishTm;
    return {
      id: entry.id,
      profile: entry.profile,
      place: entry.place,
      time:
        tm !== null ? formatRaceTime(tm) : entry.quit ? ru.results.quit : ru.results.notFinished,
      levels: timed ? String(entry.levels) : ru.results.levelsOf(entry.levels, race.levelCount),
      levelsDone: entry.levels,
      deaths: entry.deaths,
      isMe: entry.id === myId,
    };
  });
}

/** Medal for the first three places, the number otherwise, a dash without a place. */
export function placeLabel(place: number | null): string {
  if (place === null) return ru.results.notFinished;
  return ['🥇', '🥈', '🥉'][place - 1] ?? String(place);
}
